import { AlertCircle } from 'lucide-react';
import { ArrowDown, ArrowLeft, ArrowUp, Loader2, Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '~/components/ui/alert-dialog';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import { Switch } from '~/components/ui/switch';
import { getServiceMedicalTypeLabel, ServiceMedicalType } from '~/types/server-types/ServiceMedicalType';
import { getServicePillarTypeLabel, ServicePillarType } from '~/types/server-types/ServicePillarType';
import Content from '../components/layout/Content';
import { useUserStore } from '~/stores/user-store';
import { useShallow } from 'zustand/react/shallow';
import { UserRole } from '~/types/server-types/UserRole';

const Treatment = () => {
    const { userRole } = useUserStore(
        useShallow((state) => ({
            userRole: state.user?.role
        }))
    );
    const shouldDisableAllInputs = userRole !== UserRole.ADMIN;

    const params = useParams();
    const navigate = useNavigate();

    const id = params.id!;

    const treatmentQuery = trpc.treatments.getOne.useQuery(
        {
            id: id,
        },
        {
            suspense: true,
        }
    );

    const treatmentCategoriesQuery = trpc.treatmentCategories.getAll.useQuery(undefined, {
        suspense: true,
    });

    const sortedVariants = [...(treatmentQuery?.data?.data.variants || [])].sort(
        (a, b) => a.order - b.order
    );

    const updateTreatmentMutation = trpc.treatments.update.useMutation({
        onSuccess: () => {
            toast.success('Treatment updated successfully.');
            treatmentQuery.refetch();
        },
        onError: (error) => {
            toast.error(`Failed to update treatment: ${error.message}`);
        },
    });

    const updateManyOrdersMutation = trpc.treatmentVariants.updateManyOrders.useMutation({
        onSuccess: () => {
            toast.success('Orders updated successfully.');
            treatmentQuery.refetch();
        },
        onError: (error) => {
            toast.error(`Orders to update treatment: ${error.message}`);
        },
    });

    const archiveTreatmentMutation = trpc.treatments.archive.useMutation({
        onSuccess: () => {
            toast.success('Treatment archived successfully.');
        },
        onError: (error) => {
            toast.error(`Failed to archive treatment: ${error.message}`);
        },
    });

    const handleArchiveTreatment = async () => {
        if (userRole !== UserRole.ADMIN) {
            toast.error("You do not have permission to do this.");
            return;
        }
        await archiveTreatmentMutation.mutateAsync({
            id,
        });
        navigate('/treatments');
    };

    const handleUpdateTreatment = async (data: { name: string; shortDescription: string; longDescription?: string; categoryId?: number | null, servicePillarType: ServicePillarType, serviceMedicalType: ServiceMedicalType, storeIds: number[] }) => {
        if (userRole !== UserRole.ADMIN) {
            toast.error("You do not have permission to do this.");
            return;
        }
        updateTreatmentMutation.mutate({
            id,
            ...data,
        });
    };

    const reorderAllVariants = (
        variants: Array<any>,
        currentIndex: number,
        targetIndex: number
    ): Array<{ id: string, order: number }> => {
        // Create a new array with the moved item at the target position
        const newOrder = [...variants];
        const [movedItem] = newOrder.splice(currentIndex, 1);
        newOrder.splice(targetIndex, 0, movedItem);

        // Assign sequential order values (0, 1, 2, ...) to all variants
        return newOrder.map((variant, index) => ({
            id: variant.id,
            order: index
        }));
    };

    const handleMoveVariant = async (variantId: string, direction: 'up' | 'down') => {
        if (userRole !== UserRole.ADMIN) {
            toast.error("You do not have permission to do this.");
            return;
        }
        if (!treatmentQuery.data?.data.variants) return;

        // Ensure variants are sorted by current order for correct index calculation
        // When orders are equal, we'll maintain the existing array order as a secondary sort
        const sortedVariants = [...treatmentQuery.data.data.variants].sort((a, b) => {
            const orderDiff = a.order - b.order;
            // If orders are the same, maintain the order they appear in the original array
            return orderDiff !== 0 ? orderDiff : 0;
        });

        const currentIndex = sortedVariants.findIndex((v) => v.id === variantId);
        if (currentIndex === -1) {
            console.error("Variant not found for reordering");
            return;
        }

        let targetIndex: number;
        if (direction === 'up') {
            if (currentIndex === 0) return; // Cannot move the first item up
            targetIndex = currentIndex - 1;
        } else {
            if (currentIndex === sortedVariants.length - 1) return; // Cannot move the last item down
            targetIndex = currentIndex + 1;
        }

        const variantToMove = sortedVariants[currentIndex];
        const targetVariant = sortedVariants[targetIndex];

        // Calculate new order values
        let newOrders;

        // If both variants have the same order (e.g., both are 0)
        if (variantToMove.order === targetVariant.order) {
            // Create meaningful order values based on their positions
            // We'll assign orders 0, 1, 2, etc. to create a proper sequence
            newOrders = reorderAllVariants(sortedVariants, currentIndex, targetIndex);
        } else {
            // Standard case - just swap the orders
            newOrders = [
                { id: variantToMove.id, order: targetVariant.order },
                { id: targetVariant.id, order: variantToMove.order },
            ];
        }

        console.log("Updating variant orders:", newOrders); // For debugging
        await updateManyOrdersMutation.mutateAsync({ variantOrders: newOrders });
    };

    if (treatmentQuery.isLoading) {
        return (
            <div className="max-w-6xl container mx-auto">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex flex-row items-center gap-3">
                        <div className="w-8 h-8">
                            <Skeleton className="h-8 w-8 rounded-md" />
                        </div>
                        <Skeleton className="h-8 w-64 rounded" />
                        <Skeleton className="h-6 w-20 rounded-full" />
                    </div>
                </div>

                <div className="flex gap-4">
                    {/* Left Column - 75% width */}
                    <div className="w-3/4 space-y-4">
                        <Card className="bg-white shadow-sm">
                            <CardContent className="p-6">
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <Skeleton className="h-4 w-16" />
                                        <Skeleton className="h-10 w-full" />
                                    </div>

                                    <div className="space-y-2">
                                        <Skeleton className="h-4 w-32" />
                                        <Skeleton className="h-10 w-full" />
                                    </div>

                                    <div className="space-y-2">
                                        <Skeleton className="h-4 w-24" />
                                        <Skeleton className="h-32 w-full" />
                                    </div>

                                    <div className="flex justify-end mt-6">
                                        <Skeleton className="h-10 w-32" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column - 25% width */}
                    <div className="w-1/4 space-y-4">
                        <Card className="bg-white shadow-sm">
                            <CardContent className="p-6">
                                <Skeleton className="h-6 w-32 mb-4" />
                                <div className="space-y-3">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-3/4" />
                                    <Skeleton className="h-4 w-5/6" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        )
    }

    if (!treatmentQuery.data)
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className='flex flex-col items-center justify-center min-h-[60vh] text-center'>
                        <div className='p-4 bg-red-100 rounded-full mb-6'>
                            <AlertCircle className='h-16 w-16 text-red-600' />
                        </div>
                        <h1 className='text-4xl font-bold text-gray-900 mb-4'>Ups...</h1>
                        <p className='text-xl text-gray-600 mb-8'>Etwas ist schief gelaufen</p>
                        <Button variant='default' onClick={() => navigate(-1)}>
                            Zurück
                        </Button>
                    </div>
                </div>
            </Content>
        );

    return (
        <Content>
            <div className='max-w-6xl container mx-auto'>
                <div className='flex justify-between items-center mb-4'>
                    <div className='flex flex-row items-center gap-3'>
                        <Button variant='ghost' size='icon' onClick={() => navigate('/treatments')} className='w-8 h-8 border-gray-300 hover:bg-gray-50'>
                            <ArrowLeft />
                        </Button>
                        <h2 className='text-3xl font-semibold mb-1.5'>{treatmentQuery.data.data.name}</h2>
                        <Badge className='h-6 bg-green-200 text-green-800 px-2 flex items-center !py-0'>Active</Badge>
                    </div>
                </div>

                <div className='flex gap-4'>
                    {/* Left Column - 75% width */}
                    <div className='w-3/4 space-y-4'>
                        {/* Basic Information Card */}
                        <Card className='bg-white shadow-sm'>
                            <CardContent>
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        const formData = new FormData(e.currentTarget);
                                        const fields = {
                                            name: formData.get('name') as string,
                                            shortDescription: formData.get('shortDescription') as string,
                                            longDescription: formData.get('longDescription') as string,
                                            categoryId: formData.get('categoryId') ? Number(formData.get('categoryId')) : null,
                                            servicePillarType: formData.get('servicePillarType') ? formData.get('servicePillarType') as ServicePillarType : ServicePillarType.HEALTH,
                                            serviceMedicalType: formData.get('serviceMedicalType') ? formData.get('serviceMedicalType') as ServiceMedicalType : ServiceMedicalType.NOT_MEDICAL,
                                            storeIds: Array.from(formData.getAll('storeIds')).map(id => Number(id)),
                                        };
                                        handleUpdateTreatment(fields);
                                    }}
                                >
                                    <div className='space-y-6'>
                                        <div className='space-y-2'>
                                            <label htmlFor='name' className='text-sm font-medium'>
                                                Name
                                            </label>
                                            <Input
                                                id='name'
                                                name='name'
                                                defaultValue={treatmentQuery.data.data.name}
                                                className='w-full'
                                                required
                                                disabled={shouldDisableAllInputs}
                                            />
                                        </div>

                                        <div className='space-y-2'>
                                            <label htmlFor='shortDescription' className='text-sm font-medium'>
                                                Short Description
                                            </label>
                                            <Input
                                                id='shortDescription'
                                                name='shortDescription'
                                                defaultValue={treatmentQuery.data.data.shortDescription}
                                                className='w-full'
                                                disabled={shouldDisableAllInputs}
                                            />
                                        </div>

                                        <div className='space-y-2'>
                                            <label htmlFor='longDescription' className='text-sm font-medium'>
                                                Long Description
                                            </label>
                                            <textarea
                                                id='longDescription'
                                                name='longDescription'
                                                defaultValue={treatmentQuery.data.data.longDescription || ''}
                                                className={`w-full min-h-[150px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${shouldDisableAllInputs ? 'text-gray-400' : ''}`}
                                                disabled={shouldDisableAllInputs}
                                            />
                                        </div>

                                        <div className='space-y-2'>
                                            <label htmlFor='categoryId' className='text-sm font-medium'>
                                                Category
                                            </label>
                                            <Select name='categoryId' defaultValue={treatmentQuery.data.data.categoryId?.toString() || ''} disabled={shouldDisableAllInputs}>
                                                <SelectTrigger className='w-full'>
                                                    <SelectValue placeholder='Select a category' />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {treatmentCategoriesQuery.isLoading ? (
                                                        <SelectItem value='loading' disabled>
                                                            Loading categories...
                                                        </SelectItem>
                                                    ) : (
                                                        <>
                                                            {treatmentCategoriesQuery.data?.data.map((category) => (
                                                                <SelectItem key={category.id} value={category.id.toString()}>
                                                                    {category.name}
                                                                </SelectItem>
                                                            ))}
                                                        </>
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className='space-y-2'>
                                            <label htmlFor='servicePillarType' className='text-sm font-medium'>
                                                Service Pillar Type
                                            </label>
                                            <Select name='servicePillarType' defaultValue={treatmentQuery.data.data.servicePillarType || ''} disabled={shouldDisableAllInputs}>
                                                <SelectTrigger className='w-full'>
                                                    <SelectValue placeholder='Select a service pillar type' />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.values(ServicePillarType).map((pillarType) => (
                                                        <SelectItem key={pillarType} value={pillarType}>
                                                            {getServicePillarTypeLabel(pillarType)}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className='space-y-2'>
                                            <label htmlFor='serviceMedicalType' className='text-sm font-medium'>
                                                Service Medical Type
                                            </label>
                                            <Select name='serviceMedicalType' defaultValue={treatmentQuery.data.data.serviceMedicalType || ''} disabled={shouldDisableAllInputs}>
                                                <SelectTrigger className='w-full'>
                                                    <SelectValue placeholder='Select a service medical type' />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.values(ServiceMedicalType).map((medicalType) => (
                                                        <SelectItem key={medicalType} value={medicalType}>
                                                            {getServiceMedicalTypeLabel(medicalType)}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* <div className='space-y-2'>
                                            <label className='text-sm font-medium'>
                                                Stores
                                            </label>
                                            <div className='space-y-2'>
                                                {storesQuery.isLoading ? (
                                                    <div className='text-sm text-gray-500'>Loading stores...</div>
                                                ) : (
                                                    <>
                                                        {storesQuery.data?.data.map((store) => {
                                                            const isSelected = treatmentQuery.data.data.storeAssociations.some(
                                                                (assoc) => assoc.storeId === store.id
                                                            );
                                                            return (
                                                                <div key={store.id} className='flex items-center space-x-2'>
                                                                    <Checkbox 
                                                                        id={`store-${store.id}`} 
                                                                        name='storeIds' 
                                                                        value={store.id} 
                                                                        defaultChecked={isSelected}
                                                                    />
                                                                    <label 
                                                                        htmlFor={`store-${store.id}`}
                                                                        className='text-sm'
                                                                    >
                                                                        {store.name}
                                                                    </label>
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </div>
                                        </div> */}

                                        <div className='flex justify-end mt-6'>
                                            <Button
                                                type='submit'
                                                className='bg-primary-400 hover:bg-primary-500 !text-white transition-all'
                                                disabled={updateTreatmentMutation.isPending || shouldDisableAllInputs}
                                            >
                                                {updateTreatmentMutation.isPending && <Loader2 className='animate-spin mr-2 h-4 w-4' />}
                                                Save Changes
                                            </Button>
                                        </div>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>

                        {/* Treatment Steps Card */}
                        {/* <Card className='bg-white shadow-sm'>
                            <CardHeader>
                                <CardTitle>Treatment Steps</CardTitle>
                                <p className='text-sm text-muted-foreground'>Define the sequence of steps for this treatment</p>
                            </CardHeader>
                            <CardContent>
                                {treatmentQuery.data.data.variants.length > 0 && (
                                    <TreatmentSteps variantId={treatmentQuery.data.data.variants[0].id}  />
                                )}
                            </CardContent>
                        </Card> */}

                        {/* Variants Card */}
                        <Card className='bg-white shadow-sm'>
                            <CardHeader>
                                <CardTitle>Variants</CardTitle>
                            </CardHeader>
                            <CardContent className='pt-0'> {/* Adjusted padding */}
                                {sortedVariants.length === 0 ? (
                                    // ... No variants placeholder ...
                                    <div className='flex flex-col items-center justify-center py-8 text-center border-2 border-dashed border-gray-200 rounded-lg'>
                                        <h3 className='text-lg font-medium text-gray-900'>No variants yet</h3>
                                        <p className='mt-1 text-sm text-gray-500'>Add variants to this treatment</p>
                                        <Button
                                            variant='outline'
                                            className='mt-4' // Simplified style
                                            onClick={() => {
                                                if (userRole !== UserRole.ADMIN) {
                                                    toast.error("You don't have permission to do this.");
                                                    return;
                                                }
                                                navigate(`${location.pathname}/variants/new`)
                                            }}
                                            disabled={shouldDisableAllInputs}
                                        >
                                            <Plus className='mr-2 h-4 w-4' /> {/* Added margin */}
                                            Add Variant
                                        </Button>
                                    </div>
                                ) : (
                                    <div className='space-y-2'> {/* Reduced spacing between variants */}
                                        {sortedVariants.map((variant, index) => {
                                            const storeNames = variant.storeAssociations.flatMap((assoc) => assoc.store.name);
                                            const isFirst = index === 0;
                                            const isLast = index === sortedVariants.length - 1;

                                            return (
                                                <div key={variant.id} className='flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50 transition-all'> {/* Reduced padding */}
                                                    {/* Variant Info */}
                                                    <div className="flex-grow space-y-1 mr-4"> {/* Reduced spacing, added margin */}
                                                        <h3 className='font-medium text-sm'>{variant.name}</h3> {/* Slightly smaller text */}
                                                        {variant.shortDescription && <p className='text-xs text-gray-500'>{variant.shortDescription}</p>} {/* Smaller text */}
                                                        {storeNames.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 mt-1"> {/* Reduced gap/margin */}
                                                                {storeNames.map((storeName, idx) => (
                                                                    <span
                                                                        key={`${variant.id}-store-${idx}`}
                                                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" // Smaller badge
                                                                    >
                                                                        {storeName}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Action Buttons */}
                                                    <div className="flex items-center gap-1 flex-shrink-0"> {/* Added gap */}
                                                        {/* --- Move Up Button --- */}
                                                        <Button
                                                            variant='ghost'
                                                            size="icon"
                                                            className="h-8 w-8 text-gray-500 hover:text-gray-700" // Smaller icon button
                                                            onClick={() => handleMoveVariant(variant.id, 'up')}
                                                            disabled={isFirst || updateManyOrdersMutation.isPending || shouldDisableAllInputs} // Disable if first or mutation pending
                                                            aria-label="Move variant up"
                                                        >
                                                            <ArrowUp className="h-4 w-4" />
                                                        </Button>
                                                        {/* --- Move Down Button --- */}
                                                        <Button
                                                            variant='ghost'
                                                            size="icon"
                                                            className="h-8 w-8 text-gray-500 hover:text-gray-700" // Smaller icon button
                                                            onClick={() => handleMoveVariant(variant.id, 'down')}
                                                            disabled={isLast || updateManyOrdersMutation.isPending || shouldDisableAllInputs} // Disable if last or mutation pending
                                                            aria-label="Move variant down"
                                                        >
                                                            <ArrowDown className="h-4 w-4" />
                                                        </Button>
                                                        {/* --- Edit Button --- */}
                                                        <Button
                                                            variant='ghost'
                                                            size="sm" // Smaller edit button
                                                            className="h-8 px-2"
                                                            onClick={() => navigate(`${location.pathname}/variants/${variant.id}`)}
                                                            disabled={updateManyOrdersMutation.isPending} // Disable while reordering
                                                        >
                                                            Edit
                                                        </Button>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        {/* Add Variant Button (if variants exist) */}
                                        <div className='flex justify-end pt-4'>
                                            <Button
                                                variant='outline'
                                                size="sm" // Smaller add button
                                                onClick={() => {
                                                    if (userRole !== UserRole.ADMIN) {
                                                        toast.error("You don't have permission to do this.");
                                                        return;
                                                    }
                                                    navigate(`${location.pathname}/variants/new`)
                                                }}
                                                disabled={updateManyOrdersMutation.isPending || shouldDisableAllInputs} // Disable while reordering
                                            >
                                                <Plus className='mr-1 h-4 w-4' /> {/* Adjusted margin */}
                                                Add Variant
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Danger Zone Card */}
                        <Card className='bg-white shadow-sm border-red-200 mt-8'>
                            <CardHeader className='pb-3'>
                                <CardTitle className='text-red-600'>Danger Zone</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className='space-y-4'>
                                    <div className='flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50'>
                                        <div>
                                            <h3 className='font-medium text-red-600'>Delete Treatment</h3>
                                            <p className='text-sm text-red-600'>
                                                This will delete the treatment. This action cannot be undone.
                                            </p>
                                        </div>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button variant='destructive' disabled={shouldDisableAllInputs}>Delete</Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        This will archive the treatment "{treatmentQuery.data.data.name}" and hide it from the system.
                                                        This action cannot be easily undone.
                                                        Existing appointments with this treatment will STILL be in the system, so no worries.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        onClick={handleArchiveTreatment}
                                                        className='bg-red-600 hover:bg-red-700'
                                                    >
                                                        {archiveTreatmentMutation.isPending ? (
                                                            <>
                                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                Deleting...
                                                            </>
                                                        ) : (
                                                            'Delete Treatment'
                                                        )}
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column - 25% width */}
                    <div className='w-1/4 space-y-4'>
                        {/* Status Card */}
                        <Card className='bg-white shadow-sm'>
                            <CardHeader>
                                <CardTitle>Status</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className='space-y-4'>
                                    <div className='flex items-center'>
                                        <div className='w-3 h-3 rounded-full bg-green-500 mr-2'></div>
                                        <span className='text-sm font-medium'>Active</span>
                                    </div>
                                    <div className='text-sm text-gray-500'>
                                        <p>Last updated: {new Date(treatmentQuery.data.data.updatedAt).toLocaleString()}</p>
                                        <p>Created: {new Date(treatmentQuery.data.data.createdAt).toLocaleString()}</p>
                                        <p>ID: {treatmentQuery.data.data.id}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Publishing Card - Placeholder */}
                        <Card className='bg-white shadow-sm'>
                            <CardHeader>
                                <CardTitle>Publishing</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className='space-y-4'>
                                    <div className='flex items-center justify-between'>
                                        <span className='text-sm font-medium'>Visibility</span>
                                        <Switch />
                                    </div>
                                    <div className='flex items-center justify-between'>
                                        <span className='text-sm font-medium'>Featured</span>
                                        <Switch />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </Content>
    );
};

export default Treatment;
